import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  side?: 'left' | 'right';
  noPadding?: boolean;
}

const widthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  width = 'md',
  side = 'right',
  noPadding = false,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap (basic)
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div className={`absolute inset-y-0 ${side === 'left' ? 'left-0' : 'right-0'} flex max-w-full`}>
        <div
          ref={drawerRef}
          className={`w-screen ${widthClasses[width]} transform transition-transform duration-300 ease-out`}
          style={{
            transform: isOpen ? 'translateX(0)' : `translateX(${side === 'left' ? '-100%' : '100%'})`,
          }}
        >
          <div className="flex h-full flex-col bg-background shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
              <div className="min-w-0 flex-1">
                {title && (
                  <h2 className="font-serif text-[20px] font-medium text-foreground">{title}</h2>
                )}
                {subtitle && (
                  <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <span className="sr-only">Close panel</span>
                <X className="h-5 w-5" strokeWidth={1.25} aria-hidden="true" />
              </button>
            </div>

            {/* Content */}
            <div className={`flex-1 overflow-y-auto ${noPadding ? '' : 'px-4 py-4 sm:px-6'}`}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
